import { API_BASE } from "./authApi";
import {
  accountPreviewEnabled,
  accountPreviewPlans,
  previewCatalog,
  previewOpenSubscription,
  previewSubscription,
} from "../dev/preview";

/** Tier limits; `null` means unlimited. */
export interface TierLimits {
  properties: number | null;
  bank_accounts: number | null;
}

export interface ProductInfo {
  code: string;
  label: string;
  app: string;
  price_configured: boolean;
  price_copy?: string | null;
  limits?: TierLimits | null;
}

export type CustomerTier = "customer" | "customer_plus" | "customer_premium";
export type CheckoutProduct = CustomerTier | "advisor" | "ai_lotse";

export const CUSTOMER_TIERS: CustomerTier[] = ["customer", "customer_plus", "customer_premium"];

export function isCustomerTier(code: string | null | undefined): code is CustomerTier {
  return CUSTOMER_TIERS.includes(code as CustomerTier);
}

export interface AiLotseUsage {
  month_utc: string;
  input_tokens: number;
  output_tokens: number;
  estimated_cost_micro_eur: number;
  monthly_budget_micro_eur: number;
  request_count: number;
}

export interface AiLotseSubscription {
  product_code: "ai_lotse";
  status: string | null;
  entitled: boolean;
  cancel_at_period_end: boolean;
  current_period_end: number | null;
  usage: AiLotseUsage | null;
  consent_version: string | null;
  consent_granted: boolean;
}

export interface SubscriptionView {
  product_code: string | null;
  status: string | null;
  cancel_at_period_end: boolean;
  current_period_end: number | null;
  entitled: boolean;
  stripe_configured: boolean;
  products: ProductInfo[];
  ai_lotse: AiLotseSubscription;
}

export interface DownloadAsset {
  platform: string;
  label: string;
  filename: string;
  version: string;
  download_path: string;
  size_bytes: number | null;
}

export interface CatalogApp {
  app: string;
  product_code: string;
  label: string;
  entitled: boolean;
  version: string | null;
  notes?: string | null;
  published_at?: string | null;
  assets: DownloadAsset[];
}

export interface CatalogResp {
  apps: CatalogApp[];
}

export class AccountApiError extends Error {
  readonly status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = "AccountApiError";
    this.status = status;
  }
}

async function apiFetch<T>(
  path: string,
  init?: RequestInit,
  accessToken?: string | null,
): Promise<T> {
  const headers: Record<string, string> = {
    ...(init?.headers as Record<string, string> | undefined),
    ...(init?.body ? { "Content-Type": "application/json" } : {}),
  };
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
  const resp = await fetch(`${API_BASE}${path}`, {
    ...init,
    credentials: "include",
    headers,
  });
  if (!resp.ok) {
    const body = await resp.text();
    let msg = body;
    try {
      const j = JSON.parse(body) as { error?: string };
      if (j.error) msg = j.error;
    } catch {
      /* keep */
    }
    throw new AccountApiError(resp.status, msg || `HTTP ${resp.status}`);
  }
  const text = await resp.text();
  if (!text.trim()) return undefined as T;
  return JSON.parse(text) as T;
}

export function getSubscription(token: string | null): Promise<SubscriptionView> {
  if (accountPreviewEnabled()) {
    return Promise.resolve(accountPreviewPlans() ? previewOpenSubscription : previewSubscription);
  }
  return apiFetch("/billing/subscription", undefined, token);
}

export async function startCheckout(
  token: string | null,
  product: CheckoutProduct,
): Promise<{ url: string }> {
  if (accountPreviewEnabled()) {
    throw new AccountApiError(400, "In der lokalen Vorschau startet kein Bezahlvorgang.");
  }
  return apiFetch(
    "/billing/checkout-session",
    { method: "POST", body: JSON.stringify({ product }) },
    token,
  );
}

export async function openBillingPortal(token: string | null): Promise<{ url: string }> {
  if (accountPreviewEnabled()) {
    throw new AccountApiError(400, "In der lokalen Vorschau gibt es kein Abrechnungsportal.");
  }
  return apiFetch("/billing/portal-session", { method: "POST", body: "{}" }, token);
}

export function getDownloadCatalog(token: string | null): Promise<CatalogResp> {
  if (accountPreviewEnabled()) return Promise.resolve(previewCatalog);
  return apiFetch("/downloads/catalog", undefined, token);
}

export function downloadUrl(downloadPath: string): string {
  return `${API_BASE}${downloadPath}`;
}

export async function saveDownload(
  downloadPath: string,
  filename: string,
  token: string | null,
): Promise<void> {
  if (accountPreviewEnabled()) {
    throw new AccountApiError(400, "In der lokalen Vorschau liegt keine Installer-Datei.");
  }
  const headers: Record<string, string> = {};
  if (token) headers.Authorization = `Bearer ${token}`;
  const resp = await fetch(downloadUrl(downloadPath), { credentials: "include", headers });
  if (!resp.ok) throw new AccountApiError(resp.status, "Download fehlgeschlagen");
  if (resp.redirected) {
    window.location.href = resp.url;
    return;
  }
  const blob = await resp.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
